import { Ajax, Ajax2 } from '@shared/Ajax';
import * as React from 'react';
import { Dialog } from '@shared/ui-react/Dialog';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Tree } from '@shared/ui-react/Tree';
import { Notify } from '@shared/ui-native/Notify';
import { Message } from 'primereact/message';

interface IModelsList {
    matchingModels: IModel[];
    nearByModels: IModel[];
    // calculated by service
    matchingModelIds?: number[];
    nearByModelIds?: number[];
}

export const NearbyModelsService = {
    fetch: (lat: number, lon: number): Promise<IModelsList> => {
        return Ajax2({
            url: kuura.getContextPath() + '/api/1/coords/modelsAtLocation.json',
            timeout: 90000,
            data: {
                projectId: kuura.getProjectId(),
                lat,
                lon,
            },
        })
            .then(resp => {
                const matchingModelIds = resp.matchingModels.map(m => m.id);
                const nearByModelIds = resp.nearByModels.map(m => m.id);
                return {
                    ...resp,
                    matchingModelIds,
                    nearByModelIds,
                };
            })
            .catch(Notify.error);
    },
};

export const NearbyModelsDialog = {
    open: (options: { lat: number; lon: number; treeData: ITreeData; onLoad: (ids: number[], includeLogpoints: boolean) => void }) => {
        ReactUtils.safeRender(
            <Dialog title={kuura.getMessage('js_map.nearby_models.title')} closeOnEsc={true} onClose={NearbyModelsDialog.close}>
                <NearbyModelsComponent lat={options.lat} lon={options.lon} treeData={options.treeData} onLoad={options.onLoad} onClose={NearbyModelsDialog.close} />
            </Dialog>,
            document.getElementById('react-portal-root')
        );
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

interface NearbyModelsComponentProps {
    lat: number;
    lon: number;
    treeData: ITreeData;
    onLoad: (ids: number[], includeLogpoints: boolean) => void;
    onClose: () => void;
}

interface NearbyModelsComponentState {
    loading: boolean;
    matchingModelIds: number[];
    nearByModelIds: number[];
    error?: string;
}

export class NearbyModelsComponent extends React.Component<NearbyModelsComponentProps, NearbyModelsComponentState> {
    checkedMatchingModels: number[] = [];
    checkedNearbyModels: number[] = [];
    includeLogpointsInput: HTMLInputElement = null;

    state: NearbyModelsComponentState = {
        loading: true,
        matchingModelIds: [],
        nearByModelIds: [],
        error: null,
    };

    componentDidMount() {
        NearbyModelsService.fetch(this.props.lat, this.props.lon)
            .then(resp => {
                if (!resp) {
                    this.setState({ loading: false });
                    return;
                }

                // matching models start checked, nearby models only when there are no matching ones
                this.checkedMatchingModels = resp.matchingModelIds;
                this.checkedNearbyModels = resp.matchingModelIds?.length === 0 ? resp.nearByModelIds : [];

                this.setState({ loading: false, matchingModelIds: resp.matchingModelIds, nearByModelIds: resp.nearByModelIds });
            })
            .catch(() => {
                this.setState({ error: 'failed to load' });
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    }

    style = {
        body: {
            backgroundColor: 'white',
        },
        topTree: {
            marginBottom: '2em',
            paddingBottom: '2em',
            borderBottom: '1px solid #e8e8e8',
            flexBasis: '50%',
            flex: 1,
            minHeight: '15vh',
        },
    };

    render() {
        if (this.state.loading) {
            return <div className='overlay-loading no-bg' style={{ height: 300 }}></div>;
        }

        if (this.state.error) {
            return <Message severity='error' text={kuura.getMessage('js_popup.default_error')} />;
        }

        if (!this.state.matchingModelIds?.length && !this.state.nearByModelIds?.length) {
            return <div className='padded text-center'>{kuura.getMessage('js_map.nearby_models.no_models')}</div>;
        }
        return (
            <>
                <div className='card padded flex-column of-h' style={this.style.body}>
                    {this.state.matchingModelIds?.length > 0 && (
                        <div style={this.style.topTree} className='flex-column of-a'>
                            <h6>{kuura.getMessage('js_map.nearby_models.matching_models')}</h6>
                            <Tree
                                treeData={this.props.treeData}
                                childFilter={c => {
                                    if (c.tolerance) return true;
                                    return this.state.matchingModelIds.includes(c.id);
                                }}
                                onChecked={node => {
                                    this.checkedMatchingModels = node.root?.getCheckedAndUncheckedFileIds().checkedIds;
                                }}
                                checkedIds={this.checkedMatchingModels}
                                expandOnStart={'all'}
                                includesFiles={true}
                                hideEmptyFoldersAlways={true}
                                hideEmptyFolders={true}
                            />
                        </div>
                    )}
                    {this.state.nearByModelIds?.length > 0 && (
                        <div className='flex-column of-a' style={{ flexBasis: '50%', flex: 1 }}>
                            <h6>{kuura.getMessage('js_map.nearby_models.title')}</h6>
                            <Tree
                                treeData={this.props.treeData}
                                childFilter={c => {
                                    if (c.tolerance) return true;
                                    return this.state.nearByModelIds?.includes(c.id);
                                }}
                                checkedIds={this.checkedNearbyModels}
                                onChecked={node => {
                                    this.checkedNearbyModels = node.root?.getCheckedAndUncheckedFileIds().checkedIds;
                                }}
                                propagateCheckbox={'deep'}
                                expandOnStart={'all'}
                                includesFiles={true}
                                hideEmptyFoldersAlways={true}
                                hideEmptyFolders={true}
                            />
                        </div>
                    )}
                    <hr />
                    <label className='checkbox-label' style={{ flexShrink: 0 }}>
                        <input type='checkbox' ref={el => (this.includeLogpointsInput = el)} /> {kuura.getMessage('js_map.nearby_models.also_load_logpoints')}
                    </label>
                </div>

                <footer className='text-center padded' style={{ flexShrink: 0 }}>
                    <button onClick={this.props.onClose} style={{ marginRight: '1em' }}>
                        {kuura.getMessage('general.close')}
                    </button>
                    <button className='ik-button-primary' onClick={this.handleLoadModels}>
                        {kuura.getMessage('js_map.nearby_models.load_selected_models')}
                    </button>
                </footer>
            </>
        );
    }

    handleLoadModels = () => {
        this.props.onLoad([...this.checkedMatchingModels, ...this.checkedNearbyModels], this.includeLogpointsInput.checked);
    };
}
